import { gql } from '@apollo/client';
import { Box, Table, Tbody, Td, Th, Thead, Tooltip, Tr } from '@chakra-ui/react';
import { TableInstance } from 'react-table';
import { ChevronDownIcon, ChevronUpIcon } from '~components/ui/icons';
import { ZteTableFragment } from './__generated__/ZteTable.graphql';
import { Filter } from './Filtering/Filter';
import { ZteTablePageSizeSelector, ZteTablePagination } from './Pagination';
import { SearchBar } from './SearchBar';

interface Props {
  table: TableInstance<ZteTableFragment>;
}

export function ZteTable({ table }: Props) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } = table;

  return (
    <Table {...getTableProps()} variant="simple" width="100%" layout="fixed">
      <Thead>
        {headerGroups.map((headerGroup) => {
          const headerGroupProps = headerGroup.getHeaderGroupProps();

          return (
            <Tr {...headerGroupProps} key={headerGroupProps.key}>
              {headerGroup.headers.map((column) => {
                const sortProps = column.getSortByToggleProps();
                const sortColumn = sortProps.onClick;
                const headerProps = column.getHeaderProps(sortProps);

                return (
                  <Th
                    {...headerProps}
                    key={headerProps.key}
                    color={column.isSorted ? 'gray.700' : 'gray.600'}
                    minWidth={`${column.minWidth}px`}
                    maxWidth={`${column.maxWidth}px`}
                    width={column.width}
                    _hover={
                      column.disableSortBy
                        ? {}
                        : {
                            textColor: 'gray.800',
                          }
                    }
                    onClick={sortColumn}
                  >
                    <Box display="flex" flexDirection="row" alignItems="center" width="100%">
                      <Tooltip label={String(column.render('Header'))} placement="top">
                        <Box flex="1" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                          {column.render('Header')}
                        </Box>
                      </Tooltip>
                      {column.canSort && (
                        <Box>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <ChevronDownIcon display="inherit" />
                            ) : (
                              <ChevronUpIcon display="inherit" />
                            )
                          ) : (
                            <ChevronDownIcon visibility="hidden" />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Th>
                );
              })}
            </Tr>
          );
        })}
      </Thead>
      <Tbody {...getTableBodyProps()}>
        {page.map((row) => {
          prepareRow(row);
          const rowProps = row.getRowProps();

          return (
            <Tr {...rowProps} key={rowProps.key}>
              {row.cells.map((cell) => {
                const cellProps = cell.getCellProps();
                return (
                  <Tooltip label={cell.value} key={cellProps.key}>
                    <Td
                      {...cellProps}
                      key={cellProps.key}
                      minWidth={`${cell.column.minWidth}px`}
                      maxWidth={`${cell.column.maxWidth}px`}
                      width={cell.column.width}
                    >
                      {cell.render('Cell')}
                    </Td>
                  </Tooltip>
                );
              })}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
}

ZteTable.Filter = Filter;
ZteTable.Pagination = ZteTablePagination;
ZteTable.PageSizeSelector = ZteTablePageSizeSelector;
ZteTable.SearchSelector = SearchBar;

ZteTable.graphql = {
  fragments: {
    ZteTable: gql`
      fragment ZteTable on ZteEnrolment {
        id
        alias
        batchName
        serialNumber
        status
        macAddress
        customerId
        customerName
        createdAt
        assignedAt
        modelNumber
        firmwareVersion
      }
    `,
  },
};
